import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import Logo from '../ui/Logo';
import NotificationDropdown from '../notifications/NotificationDropdown';
import { FiSearch, FiShoppingCart, FiUser, FiMenu, FiX } from 'react-icons/fi';

const Header = () => {
  const { user, logout } = useAuth();
  const { getCartItemCount } = useCart();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const profileDropdownRef = useRef(null);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/courses?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close profile dropdown when user changes (login/logout)
  useEffect(() => {
    setIsProfileDropdownOpen(false);
  }, [user]);

  return (
    <header className="bg-white shadow-soft border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-5">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <Logo size="large" responsive={true} />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8 ml-8">
            <Link to="/courses" className="nav-link px-2 py-2">
              강의
            </Link>

            <a href="https://www.listeningpt.com/" target="_blank" rel="noopener noreferrer" className="nav-link px-2 py-2">
              ListeningPT
            </a>
            <a href="https://voca-pt.com/" target="_blank" rel="noopener noreferrer" className="nav-link px-2 py-2">
              VocaPT
            </a>
            <Link to="/speaking-pt" className="nav-link px-2 py-2">
              Speaking PT
            </Link>
            <Link to="/writing-pt" className="nav-link px-2 py-2">
              Writing PT
            </Link>
          </nav>

          {/* Search Bar */}
          <div className="hidden md:flex flex-1 max-w-md mx-8">
            <form onSubmit={handleSearch} className="relative w-full">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiSearch className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="강의 검색..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
              />
            </form>
          </div>

          {/* Right side items */}
          <div className="flex items-center space-x-4">
            {/* Notifications */}
            <NotificationDropdown />
            
            {/* Cart */}
            <Link to="/cart" className="relative p-2 text-gray-600 hover:text-primary-800 transition-colors duration-200">
              <FiShoppingCart className="h-6 w-6" />
              {user && getCartItemCount() > 0 && (
                <span className="absolute -top-1 -right-1 bg-accent-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {getCartItemCount()}
                </span>
              )}
            </Link>

            {/* User Menu */}
            {user ? (
              <div className="relative" ref={profileDropdownRef}>
                <button
                  onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                  className="flex items-center space-x-2 p-2 text-gray-600 hover:text-primary-800 transition-colors duration-200 rounded-lg hover:bg-gray-50"
                >
                  <FiUser className="h-6 w-6" />
                  <span className="hidden sm:block font-medium">{user.name}</span>
                </button>

                {/* Dropdown Menu */}
                {isProfileDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-medium border border-gray-200 py-2 z-50">
                    <Link
                      to={user?.role === 'admin' ? '/admin-dashboard' : '/student-dashboard'}
                      className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                      onClick={() => setIsProfileDropdownOpen(false)}
                    >
                      {user?.role === 'admin' ? '관리자 대시보드' : '학습 대시보드'}
                    </Link>
                    <Link
                      to="/profile"
                      className="block px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                      onClick={() => setIsProfileDropdownOpen(false)}
                    >
                      프로필
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                    >
                      로그아웃
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <Link
                  to="/login"
                  className="text-gray-600 hover:text-primary-800 font-medium transition-colors duration-200"
                >
                  로그인
                </Link>
                <Link
                  to="/register"
                  className="btn-primary"
                >
                  회원가입
                </Link>
              </div>
            )}

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-gray-600 hover:text-primary-800 transition-colors duration-200"
            >
              {isMobileMenuOpen ? <FiX className="h-6 w-6" /> : <FiMenu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 py-4">
            <nav className="flex flex-col space-y-4">
              <Link
                to="/courses"
                className="nav-link px-2 py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                강의
              </Link>

              <a
                href="https://www.gyeongcheong.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="nav-link px-2 py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                ListeningPT
              </a>
              <a
                href="https://voca-pt.vercel.app/"
                target="_blank"
                rel="noopener noreferrer"
                className="nav-link px-2 py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                VocaPT
              </a>
              <Link
                to="/speaking-pt"
                className="nav-link px-2 py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Speaking PT
              </Link>
              <Link
                to="/writing-pt"
                className="nav-link px-2 py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Writing PT
              </Link>
            </nav>

            {/* Mobile Search */}
            <div className="mt-4 px-2">
              <form onSubmit={handleSearch} className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiSearch className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="강의 검색..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                />
              </form>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header; 